import React from 'react';
import {
  Eye,
  CreditCard,
  TrendingUp,
  Target,
  FileText,
  Shield,
  Zap,
  BarChart3,
  Bell,
} from 'lucide-react';
import { useInView } from 'react-intersection-observer';

const Features = () => {
  const features = [
    {
      icon: <Eye className='w-8 h-8' />,
      title: 'Saldo na Hora',
      description:
        'Veja todos os seus saldos em tempo real. Sem esperar, sem surpresas, sem dor de cabeça.',
      color: 'bg-[#13304f] text-[#19ab9d]',
    },
    {
      icon: <CreditCard className='w-8 h-8' />,
      title: 'Contas e Cartões Sem Limites',
      description:
        'Cadastre todas as contas e cartões que quiser. Zero restrições, zero stress.',
      color: 'bg-[#19ab9d] text-white',
    },
    {
      icon: <TrendingUp className='w-8 h-8' />,
      title: 'Previsão de Receitas',
      description:
        'Saiba antes de receber. O app mostra quanto vai entrar no seu bolso e quando.',
      color: 'bg-[#13304f] text-[#19ab9d]',
    },
    {
      icon: <BarChart3 className='w-8 h-8' />,
      title: 'Controle de Gastos',
      description:
        'Defina limites, acompanhe o consumo em tempo real e nunca mais estoure seu orçamento sem perceber.',
      color: 'bg-[#19ab9d] text-white',
    },
    {
      icon: <Target className='w-8 h-8' />,
      title: 'Metas que Motivam',
      description:
        'Poupar não precisa ser chato. Visualize seu progresso e sinta cada conquista.',
      color: 'bg-[#13304f] text-[#19ab9d]',
    },
    {
      icon: <FileText className='w-8 h-8' />,
      title: 'Faturas Sempre em Dia',
      description:
        'Nunca mais pague juros ou esqueça um vencimento. Receba lembretes antes do prazo.',
      color: 'bg-[#19ab9d] text-white',
    },
    {
      icon: <Bell className='w-8 h-8' />,
      title: 'Alertas Inteligentes',
      description:
        'Notificações sobre contas a pagar, gastos chegando ao limite e metas financeiras, direto no seu celular.',
      color: 'bg-[#13304f] text-[#19ab9d]',
    },
    {
      icon: <Shield className='w-8 h-8' />,
      title: 'Segurança de Verdade',
      description:
        'Todos os dados criptografados e nunca compartilhados. Simples, seguro e confiável.',
      color: 'bg-[#19ab9d] text-white',
    },
    {
      icon: <Zap className='w-8 h-8' />,
      title: 'Velocidade que Impressiona',
      description:
        'Abre em segundos, navega sem travar e responde instantaneamente. Como você precisa.',
      color: 'bg-[#13304f] text-[#19ab9d]',
    },
  ];

  return (
    <section id='funcionalidades' className='py-40 bg-white'>
      <div className='container mx-auto px-4'>
        {/* Header */}
        <div className='text-center mb-16'>
          <h2 className='text-4xl lg:text-5xl font-bold mb-6'>
            Tudo que você precisa para
            <span className='gradient-text block'>controlar suas finanças</span>
          </h2>
          <p className='text-xl text-gray-600 max-w-3xl mx-auto'>
            Cada recurso foi feito para você{' '}
            <strong>
              ver seu dinheiro em tempo real, evitar surpresas e tomar decisões
              sem esforço.
            </strong>
          </p>
        </div>

        {/* Features Grid */}
        <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8'>
          {features.map((feature, index) => {
            const { ref, inView } = useInView({
              triggerOnce: false, // anima sempre que entrar na tela
              threshold: 0.2, // 20% do card visível dispara a animação
            });

            return (
              <div
                key={index}
                ref={ref}
                className={`feature-card group p-6 rounded-2xl shadow-lg transition-all duration-500 cursor-pointer transform 
                  ${
                    inView
                      ? 'opacity-100 translate-y-0 animate-fade-slide-up'
                      : 'opacity-0 translate-y-10'
                  }
                  hover:shadow-2xl hover:translate-y-[-2px] hover:scale-[1.02]`}
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div
                  className={`inline-flex p-3 rounded-xl ${feature.color} mb-4 group-hover:scale-110 transition-transform duration-300`}
                >
                  {feature.icon}
                </div>

                <h3 className='text-xl font-bold mb-3 text-gray-800'>
                  {feature.title}
                </h3>

                <p className='text-gray-700 leading-relaxed'>
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className='text-center mt-16'>
          <div className='bg-gradient-to-r from-blue-500 to-emerald-500 text-white rounded-2xl p-8 max-w-4xl mx-auto shadow-xl'>
            <h3 className='text-2xl lg:text-3xl font-bold mb-4'>
              Experimente tudo agora
            </h3>
            <p className='text-blue-100 mb-6 text-lg'>
              Controle suas finanças com todas as funcionalidades essenciais
              desbloqueadas, totalmente gratuito e seguro.{' '}
              <strong>Simples, rápido e pronto para o seu dia a dia.</strong>
            </p>
            <a
              href='https://play.google.com/store/apps/details?id=br.android.finziin'
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center bg-white text-blue-600 font-bold py-4 px-8 rounded-xl hover:bg-gray-100 transition-colors duration-300 shadow-lg'
            >
              <CreditCard className='w-5 h-5 mr-2' />
              Começar Agora - É Grátis!
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;
